

import React, { useState, useRef, useMemo, memo, useContext } from 'react';
import styled from 'styled-components';
import _memoize from 'lodash.memoize';
import Mustache from 'mustache';
import { useEditModeManager } from '../../../../../../../common/hooks';
import editModeManager from '../../../../../../../common/utils/EditModeManager';

import { purifyHTML } from '../../../../../../../common/utils';
import { OptionsBlock, NoEditMode } from '../Actions';
import { TextColor, Visible } from '../Actions/components/OptionsList';
import { InputBase, RelativeBlock, RelativeSpanBlock } from './components/common/StyledEditables';

import { ActionTypes } from '../../../../types';
import useOuterClick from '../../../../hooks/useOuterClick';
import { dataAdapter } from '../../../../contexts/dataAdapter';

const shared = `
  width: 100%;
  height: inherit;
  position: absolute;
  object-fit: cover;
  z-index: 0;
  mix-blend-mode: soft-light;
`;

const TextBackgroundImage = styled.img`${shared}`;

const TextBackgroundVideo = styled.video`${shared}`;

const TextBase = styled.p.attrs((props) => ({
  style: {
    ...props.style,
    ...props.conditionalStyle,
    ...(!props.isVisible ? {
      opacity: '0.25'
    } : {})
  }
}))`${(props) => props.computedStyle}`;














const Text = (props) => {
  const {
    title,
    style,
    computedStyle,
    moduleId,
    rowId,
    columnId,
    isEditable,
    isVisible,
    hideVisibleOption,
    handleTemplateChange
  } = props;

  const { text = '', className = '', imageSource, videoSource } = title;

  const titleProps = { style, computedStyle, className };
  const { replaceVariables } = useContext(dataAdapter);

  const [canEdit, setShowEdit] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [textValue, setTextValue] = useState(text);
  const [isColorPickerVisible, setColorPicker] = useState(false);

  const componentId = useMemo(() => `text-${moduleId || 'unknown'}-${rowId || 'unknown'}-${columnId || 'unknown'}`, [moduleId, rowId, columnId]);

  const inputRef = useRef();
  const componentRef = useRef();

  const showEdit = () => {
    editModeManager.setActiveComponent(componentId);
    setShowEdit(true);
  };

  const hideEdit = () => {
    editModeManager.clearActiveComponent();
    setShowEdit(false);
  };

  const showColorPicker = () => setColorPicker(true);
  const hideColorPicker = () => setColorPicker(false);

  const handleOuterClick = () => {
    if (canEdit) {
      hideEdit();
      hideColorPicker();
    }

    if (canModify) {
      setCanModify(false);
    }
  };

  useOuterClick({
    dependencies: [canEdit, canModify],
    callback: handleOuterClick,
    innerRef: componentRef
  });

  useEditModeManager({
    componentId,
    callbackToHide: () => {
      setShowEdit(false);
      hideColorPicker();
      setCanModify(false);
    },
    optionalConditionToHide: canEdit || canModify,
    dependencies: [canEdit, canModify]
  });

  const setRef = (node) => {
    inputRef.current = node;

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const updateTitleText = _memoize(() => (e) => {
    const inputValue = e.target.value;

    setTextValue(inputValue);

    handleTemplateChange(ActionTypes.UPDATE_BLOCK, { data: {
        moduleDescriptorValueToSet: {
          title: {
            imageSource,
            videoSource,
            className,
            text: inputValue
          }
        }
      } });
  });

  const setIsVisible = () => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, { data: {
        overrides: {
          isVisible: !isVisible
        }
      } });
    hideColorPicker();
  };

  const handlePickerColorChange = ({ hex = '' } = {}) => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, { data: {
        moduleDescriptorValueToSet: {
          style: {
            ...style,
            color: hex.toUpperCase()
          }
        }
      } });
  };

  const handleBlockClick = (e) => {
    e.stopPropagation();

    if (isEditable) {
      showEdit();
    }

    setCanModify(true);
    return void 0;
  };

  const options = [
  ...(!hideVisibleOption ? [
  {
    action: setIsVisible,
    label:
    <Visible isVisible={isVisible} />

  }] :
  []),
  {
    action: isColorPickerVisible ? hideColorPicker : showColorPicker,
    isActive: isColorPickerVisible,
    label:
    <TextColor active={isColorPickerVisible} style={style} />

  }];


  const colorPickerOptions = useMemo(() => [
  {
    visible: isColorPickerVisible,
    initialValue: style.color || '#1A1F24',
    style: {
      position: 'absolute',
      top: '40px',
      zIndex: '9',
      left: '-35px'
    }
  }],
  [isColorPickerVisible, style]);

  const replacedText = useMemo(() => {
    const breakText = text.replace(/(?:\r\n|\r|\n)/g, '<br/>');
    try {
      const mustacheTemplate = breakText.replace(/\$\{(\w+)\}/g, '{{$1}}');
      return Mustache.render(mustacheTemplate, replaceVariables);
    } catch (e) {
      console.error(e);
      return breakText;
    }
  }, [text, replaceVariables]);

  return (
    <RelativeBlock ref={componentRef}>
      {imageSource && <TextBackgroundImage src={imageSource} alt="default" />}
      {videoSource &&
      <TextBackgroundVideo loop muted autoPlay>
        <source src={videoSource} />
      </TextBackgroundVideo>}

      {isEditable && canEdit ?
      <span className="edit-actions">
          <InputBase
          ref={(node) => setRef(node)}
          onClick={(e) => e.stopPropagation()}
          onChange={updateTitleText()}
          value={textValue}
          isVisible={isVisible}
          {...titleProps} />

          <OptionsBlock
          colorPickerOptions={colorPickerOptions}
          handlePickerColorChange={handlePickerColorChange}
          options={options} />

        </span> :

      <div
        onClick={handleBlockClick}
        tabIndex="0"
        role="button"
        aria-pressed="false"
        onKeyDown={() => undefined}>

          <TextBase
          className={className}
          style={style}
          isVisible={isVisible}
          computedStyle={computedStyle}
          conditionalStyle={imageSource || videoSource ? {
            position: 'relative',
            display: 'grid',
            height: '100%',
            placeItems: 'center'
          } : {}}>

            <RelativeSpanBlock dangerouslySetInnerHTML={{ __html: purifyHTML(replacedText) }} />
            {canModify && <NoEditMode className={className} />}
          </TextBase>
        </div>}

    </RelativeBlock>);

};

export default memo(Text);